import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { FilterSelect } from '../../components/filters/FilterSelect';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Pill } from '../../components/Pill';
import { RowActionsMenu } from '../../components/RowActionsMenu';
import { type ColumnLayout, DataTable } from '../../components/Table';
import {
  listCategories,
  listProducts,
  listProductsPage,
} from '../../data/fetchers';
import { deleteProduct } from '../../data/mutators';
import { fmtAmount } from '../../lib/format';
import { canEditMasterData } from '../../lib/permissions';
import { useSession } from '../../stores/session';
import { ProductHeader } from './ProductHeader';
import { classifyPriceHealth, derivedUnitCost } from './productUnitPricing';

interface ProductRow {
  id: string;
  name: string;
  category: string;
  baseUnit: string;
  active: boolean;
}

const PAGE_SIZE = 50;

const STATUS_FILTERS = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'active', label: 'เปิดขาย' },
  { value: 'inactive', label: 'ปิดใช้งาน' },
] as const;

export function ProductListView() {
  const navigate = useNavigate();
  const user = useSession((s) => s.user);
  const canEdit = user ? canEditMasterData(user) : false;
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const catalog = useQuery({ queryKey: ['products'], queryFn: listProducts });
  const selectedProduct = catalog.data?.find(
    (product) => product.id === selectedId,
  );
  const [deleteTarget, setDeleteTarget] = useState<ProductRow | null>(null);
  const qc = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products-page'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      setSelectedId(null);
      setDeleteTarget(null);
    },
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
  });
  const trimmedSearch = search.trim();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['products-page', page, trimmedSearch, categoryId, status],
    queryFn: () =>
      listProductsPage({
        page,
        pageSize: PAGE_SIZE,
        search: trimmedSearch || undefined,
        categoryId: categoryId || undefined,
        status,
      }),
  });
  useEffect(() => {
    setPage(1);
  }, [trimmedSearch, categoryId, status]);

  const rows = useMemo<ProductRow[]>(() => {
    return (data?.items ?? []).map((p) => ({
      id: p.id,
      name: p.name,
      category: p.categoryName ?? p.categoryId ?? '-',
      baseUnit: p.baseUnitName ?? '-',
      active: p.active,
    }));
  }, [data?.items]);

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (!isPending && page > lastPage) {
      setPage(lastPage);
    }
  }, [isPending, lastPage, page]);

  const columns = useMemo<ColumnDef<ProductRow, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'ชื่อสินค้า',
        enableSorting: false,
        meta: { className: 'bo-table-card-title' } satisfies ColumnLayout,
        cell: ({ row }) => (
          <button
            type="button"
            className="flex min-h-11 flex-col justify-center text-left hover:text-primary focus-visible:underline"
            onClick={() => setSelectedId(row.original.id)}
          >
            <span className="font-semibold leading-snug">
              {row.original.name}
            </span>
            <span className="text-xs leading-snug text-ink-3">
              {row.original.id}
            </span>
          </button>
        ),
      },
      { accessorKey: 'category', header: 'หมวดหมู่', enableSorting: false },
      { accessorKey: 'baseUnit', header: 'หน่วยฐาน', enableSorting: false },
      {
        id: 'price',
        header: 'ราคากลาง (บาท/หน่วย)',
        meta: { align: 'right' } satisfies ColumnLayout,
        cell: ({ row }) => {
          const product = catalog.data?.find(
            (item) => item.id === row.original.id,
          );
          const unit =
            product?.units.find((item) => item.id === product.baseUnitId) ??
            product?.units.find((item) => item.factor === 1);
          return (
            <span className="num">
              {unit ? `${fmtAmount(unit.price)} / ${unit.unit}` : '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'active',
        enableSorting: false,
        header: 'สถานะ',
        cell: (c) =>
          c.getValue<boolean>() ? (
            <Pill tone="ok">เปิดขาย</Pill>
          ) : (
            <Pill tone="neutral">ปิดใช้งาน</Pill>
          ),
      },
      {
        id: 'go',
        header: 'การจัดการ',
        // w-px + nowrap shrinks the column to its buttons.
        meta: {
          align: 'right',
          className: 'w-px whitespace-nowrap',
        } satisfies ColumnLayout,
        cell: (c) => (
          <div className="flex items-center justify-end gap-1">
            <Btn
              size="sm"
              onClick={() => navigate(`/products/${c.row.original.id}`)}
            >
              {canEdit ? 'แก้ไข' : 'ดู'}
            </Btn>
            {canEdit && c.row.original.active ? (
              <RowActionsMenu
                label={`ตัวเลือกเพิ่มเติม: ${c.row.original.name}`}
                actions={[
                  {
                    label: 'ปิดใช้งาน',
                    tone: 'danger',
                    disabled: deleteMutation.isPending,
                    onSelect: () => setDeleteTarget(c.row.original),
                  },
                ]}
              />
            ) : (
              // Keeps the แก้ไข buttons aligned across rows.
              <span aria-hidden="true" className="hidden w-11 md:block" />
            )}
          </div>
        ),
      },
    ],
    [navigate, canEdit, catalog.data, deleteMutation.isPending],
  );

  return (
    <div className="bo-fade">
      <ProductHeader
        title="รายการสินค้า"
        subtitle="ค้นหา ตรวจสอบสถานะ และจัดการข้อมูลสินค้าที่ใช้หน้าร้าน"
        actions={
          canEdit ? (
            <div className="flex flex-wrap gap-2">
              <Btn kind="default" onClick={() => navigate('/products/import')}>
                นำเข้า Excel
              </Btn>
              <Btn kind="primary" onClick={() => navigate('/products/new')}>
                + เพิ่มสินค้า
              </Btn>
            </div>
          ) : undefined
        }
      />
      <div className="mb-4 grid gap-3 rounded-card border border-line bg-surface p-4 md:grid-cols-[minmax(220px,1fr)_220px_220px] md:items-end">
        <Field label="ค้นหาสินค้า/บาร์โค้ด">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="เช่น โค้ก หรือ 885..."
            aria-label="ค้นหาสินค้า"
          />
        </Field>
        <FilterSelect
          label="หมวดหมู่"
          value={categoryId}
          onChange={setCategoryId}
          options={[
            { value: '', label: 'ทุกหมวดหมู่' },
            ...categories.map((category) => ({
              value: category.id,
              label: category.name,
            })),
          ]}
        />
        <FilterSelect
          label="สถานะ"
          value={status}
          onChange={(value) => setStatus(value as typeof status)}
          options={[...STATUS_FILTERS]}
        />
      </div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm-label">
        <span role="status" className="font-semibold text-ink">
          {isPending ? 'กำลังโหลดสินค้า' : `${total} รายการ`}
        </span>
        <span className="text-ink-3">ราคากลางทุกสาขา · ราคาต่อหน่วยฐาน</span>
      </div>
      {catalog.error && (
        <p role="alert" className="mb-4 text-sm-label text-danger">
          โหลดรายละเอียดราคาไม่สำเร็จ{' '}
          <button className="underline" onClick={() => void catalog.refetch()}>
            ลองใหม่
          </button>
        </p>
      )}
      {error ? (
        <div role="alert" className="bo-card p-6 text-danger">
          โหลดสินค้าไม่สำเร็จ <Btn onClick={() => void refetch()}>ลองใหม่</Btn>
        </div>
      ) : isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-10 rounded-card bg-line-2 animate-pulse"
            />
          ))}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            pageSize={PAGE_SIZE}
            mobileCards
          />
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm-label text-ink-3">
              หน้า {page} / {lastPage}
            </span>
            <div className="flex gap-2">
              <Btn
                kind="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ก่อนหน้า
              </Btn>
              <Btn
                kind="ghost"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                ถัดไป
              </Btn>
            </div>
          </div>
        </>
      )}

      <Modal
        open={selectedId !== null}
        variant="drawer"
        title={
          selectedProduct?.name ??
          rows.find((row) => row.id === selectedId)?.name ??
          'รายละเอียดสินค้า'
        }
        description={selectedId}
        onClose={() => setSelectedId(null)}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Btn onClick={() => setSelectedId(null)}>ปิด</Btn>
            {canEdit && selectedProduct && (
              <Btn
                kind="primary"
                onClick={() => navigate(`/products/${selectedId}`)}
              >
                แก้ไขสินค้า
              </Btn>
            )}
          </div>
        }
      >
        {catalog.isPending ? (
          <div
            role="status"
            className="h-48 animate-pulse rounded-card bg-bg-warm"
          >
            กำลังโหลดรายละเอียดสินค้า
          </div>
        ) : catalog.error ? (
          <div role="alert">
            โหลดรายละเอียดไม่สำเร็จ{' '}
            <Btn onClick={() => void catalog.refetch()}>ลองใหม่</Btn>
          </div>
        ) : selectedProduct ? (
          <div className="space-y-6">
            <dl className="grid grid-cols-2 gap-5 border-b border-line pb-6">
              <div>
                <dt className="text-sm-label text-ink-2">หมวดหมู่</dt>
                <dd className="mt-1">
                  {categories.find(
                    (item) => item.id === selectedProduct.categoryId,
                  )?.name ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-sm-label text-ink-2">สถานะ</dt>
                <dd className="mt-1">
                  <Pill tone={selectedProduct.active ? 'ok' : 'neutral'}>
                    {selectedProduct.active ? 'เปิดขาย' : 'ปิดใช้งาน'}
                  </Pill>
                </dd>
              </div>
              <div>
                <dt className="text-sm-label text-ink-2">หน่วยฐาน</dt>
                <dd className="mt-1">{selectedProduct.baseUnit}</dd>
              </div>
              <div>
                <dt className="text-sm-label text-ink-2">สต๊อกขั้นต่ำ</dt>
                <dd className="mt-1 num">
                  {selectedProduct.minStock} {selectedProduct.baseUnit}
                </dd>
              </div>
            </dl>
            <section>
              <h2 className="text-h3 font-semibold">หน่วยขายและราคา</h2>
              <p className="mt-1 text-sm-label text-ink-3">ราคากลางทุกสาขา</p>
              <div className="mt-5 divide-y divide-line">
                {selectedProduct.units.map((unit, index) => {
                  const cost = derivedUnitCost(
                    selectedProduct.cost ?? 0,
                    unit.factor,
                  );
                  const health = classifyPriceHealth(unit.price, cost);
                  return (
                    <article
                      key={unit.id ?? index}
                      className="grid grid-cols-[minmax(0,1fr)_auto] gap-4 py-5 first:pt-0"
                    >
                      <div className="min-w-0">
                        <h3 className="font-semibold">{unit.unit}</h3>
                        <p className="mt-1 text-sm-label text-ink-2">
                          {unit.factor === 1
                            ? 'หน่วยฐาน'
                            : `1 ${unit.unit} = ${unit.factor} ${selectedProduct.baseUnit}`}
                        </p>
                        <p className="mt-3 break-all font-mono text-sm-label text-ink-2">
                          {unit.barcode || 'ไม่มีบาร์โค้ด'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="num font-semibold">
                          {fmtAmount(unit.price)} บาท
                        </p>
                        <p className="mt-1 text-sm-label text-ink-2">
                          {cost > 0
                            ? `ทุน ${fmtAmount(cost)} บาท`
                            : 'ยังไม่มีข้อมูลต้นทุน'}
                        </p>
                        {health.marginPct !== null && (
                          <p
                            className={`mt-1 num text-sm-label ${health.status === 'loss' ? 'text-danger' : health.status === 'low-margin' ? 'text-warn' : 'text-ok'}`}
                          >
                            กำไรขั้นต้น {health.marginPct.toFixed(1)}%
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
            <p className="rounded-card bg-bg-warm p-4 text-sm-label text-ink-2">
              กำไรคำนวณจากราคากลางและต้นทุนสินค้า
              ราคาเฉพาะสาขาจัดการในเมนูราคาแยกสาขา
            </p>
          </div>
        ) : (
          <p>ไม่พบข้อมูลสินค้านี้</p>
        )}
      </Modal>

      <Modal
        open={!!deleteTarget}
        title="ปิดใช้งานสินค้า"
        description="สินค้าจะไม่แสดงสำหรับการขายใหม่ แต่ข้อมูลในประวัติเดิมจะยังอยู่ครบ"
        tone="danger"
        onClose={() => setDeleteTarget(null)}
        footer={
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Btn type="button" onClick={() => setDeleteTarget(null)}>
              ยกเลิก
            </Btn>
            <Btn
              kind="danger"
              type="button"
              disabled={!deleteTarget || deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
              }}
            >
              {deleteMutation.isPending ? 'กำลังปิดใช้งาน...' : 'ปิดใช้งาน'}
            </Btn>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-body text-ink-2">
            ต้องการปิดใช้งาน <strong>{deleteTarget?.name}</strong> ใช่หรือไม่?
          </p>
          {deleteMutation.error ? (
            <p role="alert" className="text-body font-semibold text-danger">
              {deleteMutation.error instanceof Error
                ? deleteMutation.error.message
                : 'ปิดใช้งานสินค้าไม่สำเร็จ'}
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
